import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import ActionsMenu from "./ActionsMenu";
import Footer from "../../../examples/Footer";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ExamTypeSelect from "./ExamTypeSelect";

export default function ExamExternalSchedule() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [allRows, setAllRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [examModes, setExamModes] = useState([]);
  const [errors, setErrors] = useState({});
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const openMenu = Boolean(anchorEl);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const user = JSON.parse(sessionStorage.getItem("user"));
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;

  const [form, setForm] = useState({
    program_id: "",
    exam_type_id: [],
    exam_mode_id: "",
    assessment_type: "",
    exam_month: "",
    exam_year: "",
    exam_start_date: "",
    exam_end_date: "",
    start_enroll_date: "",
    end_enroll_date: "",
    exclude_dates: [],
    workflow_status: "DRAFT",
    remarks: "",
  });

  // ✅ Snackbar
  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ✅ Fetch Data
  const fetchData = async () => {
    setLoading(true);

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/exam-external-schedule/${clientId}/${collegeId}`
      );
      if (data.success) {
        /* const mappedRows = data.data.items.map((r) => ({
          id: r.exam_schedule_master_id,
          ...r,

          // year & sem come from backend now
          year_number: r.year_number || "",
          semester_number: r.semester_number || "",

          exam_start_date: r.exam_start_date
            ? new Date(r.exam_start_date).toISOString().split("T")[0]
            : "",
          exam_end_date: r.exam_end_date
            ? new Date(r.exam_end_date).toISOString().split("T")[0]
            : "",
          start_enroll_date: r.start_enroll_date
            ? new Date(r.start_enroll_date).toISOString().split("T")[0]
            : "",
          end_enroll_date: r.end_enroll_date
            ? new Date(r.end_enroll_date).toISOString().split("T")[0]
            : "",
          created_on: r.created_on
            ? new Date(r.created_on).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            : "",
        })); */
        const mappedRows = data.data.items.flatMap((r) =>
          (r.exam_schedule_details?.length
            ? r.exam_schedule_details
            : [{ exam_schedule_detail_id: null, year_number: "", semester_number: "" }]
          ).map((d) => ({
            id: `${r.exam_schedule_master_id}-${d.exam_schedule_detail_id || "0"}`,

            ...r,

            exam_schedule_detail_id: d.exam_schedule_detail_id,
            year_number: d.year_number,
            semester_number: d.semester_number,

            exam_start_date: r.exam_start_date
              ? new Date(r.exam_start_date).toISOString().split("T")[0]
              : "",

            exam_end_date: r.exam_end_date
              ? new Date(r.exam_end_date).toISOString().split("T")[0]
              : "",

            start_enroll_date: r.start_enroll_date
              ? new Date(r.start_enroll_date).toISOString().split("T")[0]
              : "",

            end_enroll_date: r.end_enroll_date
              ? new Date(r.end_enroll_date).toISOString().split("T")[0]
              : "",

            created_on: r.created_on
              ? new Date(r.created_on).toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "",
          }))
        );
        setAllRows(mappedRows);
        setRows(mappedRows);
        setRowCount(mappedRows.length);
      } else {
        showSnackbar(data.message || "Failed to load data", "error");
      }
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    if (!collegeId || !clientId) return; // wait until both are set

    const fetchPrograms = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/programs/${clientId}`, {
          params: { college_id: collegeId }, // ✅ cleaner way
          headers: {
            "Cache-Control": "no-cache", // 🔑 force fresh data if needed
          },
        });
        setPrograms(res.data);
        axios
          .get(`${API_BASE_URL}/api/exam-types/${clientId}/${collegeId}`)
          .then((res) => setExamTypes(res.data.data.items))
          .catch((err) => console.error(err));

        axios
          .get(`${API_BASE_URL}/api/exam-modes/${clientId}/${collegeId}`)
          .then((res) => setExamModes(res.data.data.items))
          .catch((err) => console.error(err));
      } catch (err) {
        console.error("Error fetching programs:", err);
      }
    };

    fetchPrograms();
  }, [collegeId, clientId]);
  // ✅ Search filter
  useEffect(() => {
    if (!search) {
      setRows(allRows);
    } else {
      const s = search.toLowerCase();
      setRows(
        allRows.filter(
          (r) =>
            r.exam_month?.toLowerCase().includes(s) ||
            r.exam_year?.toString().includes(s) ||
            r.assessment_type?.toLowerCase().includes(s) ||
            r.workflow_status?.toLowerCase().includes(s)
        )
      );
    }
  }, [search, allRows]);

  const handleOpenAdd = () => {
    setEditing(null);
    setForm({
      program_id: "",
      exam_type_id: "",
      exam_mode_id: "",
      assessment_type: "",
      exam_month: "",
      exam_year: "",
      exam_start_date: "",
      exam_end_date: "",
      start_enroll_date: "",
      end_enroll_date: "",
      exclude_dates: "",
      workflow_status: "DRAFT",
      remarks: "",
    });
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditing(row);
    setForm(row);
    setOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm("Delete this exam schedule?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/api/exam-external-schedule/${clientId}/${collegeId}/${row.exam_schedule_master_id}`
      );
      fetchData();
      showSnackbar("Exam schedule deleted successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Delete failed", "error");
    }
  };
  const handleGenerateTimetable = async (row) => {
    if (!window.confirm("Generate timetable for this exam schedule?")) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-timetables/${clientId}/${collegeId}/${row.exam_schedule_master_id}`,
        {
          created_by: user.user_email,
        }
      );

      fetchData(); // refresh grid if new timetables affect list
      showSnackbar("Timetable generated successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Timetable generation failed", "error");
      if (err.response) {
        const backendMessage = err.response.data?.message;

        if (backendMessage?.includes("Foreign key constraint violated")) {
          // Specific handling for FK errors
          showSnackbar(
            "Cannot delete timetable because it is linked with other exam records. Remove related records first.",
            "warning"
          );
        } else {
          showSnackbar(
            backendMessage || `Request failed with status ${err.response.status}`,
            "error"
          );
        }
      } else if (err.request) {
        showSnackbar("Network error: Server not responding", "error");
      } else {
        showSnackbar(`Unexpected error: ${err.message}`, "error");
      }
    }
  };

  const handleGenerateSeatingPlan = async (row) => {
    if (!window.confirm("Generate Seating Plan for this exam schedule?")) return;

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-seating-plan/generate/${clientId}/${collegeId}/${row.exam_schedule_master_id}`,
        {
          created_by: user.user_email,
        }
      );

      fetchData(); // refresh grid if new timetables affect list
      showSnackbar("Seating Plan  generated successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || "Seating Plan generation failed", "error");
    }
    downloadSeatingPlanNew(row);
    downloadAttendanceNew(row);
    downloadAbstractSeatingPlanNew(row);
    downloadDFormNew(row);
  };

  const downloadSeatingPlanNew = async (row) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/exam-seating-plan/download/${clientId}/${collegeId}/${row.exam_schedule_master_id}`,
        {
          responseType: "blob", // <-- important for binary files
        }
      );

      const fileName =
        `${row.exam_schedule_master_id}_${row.exam_month}_${row.exam_type_name}_Seating_Plan.pdf`.replace(
          /\s+/g,
          "_"
        );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(url, `_blank_${fileName}`);
      //const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading seating plan:", err);
    }
  };

  const downloadAttendanceNew = async (row) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/exam-seating-plan/attendance/${clientId}/${collegeId}/${row.exam_schedule_master_id}`,
        {
          responseType: "blob", // <-- important for binary files
        }
      );

      const fileName =
        `${row.exam_schedule_master_id}_${row.exam_month}_${row.exam_type_name}_AttendanceSheet.pdf`.replace(
          /\s+/g,
          "_"
        );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(url, `_blank_${fileName}`);

      //const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading attendance:", err);
    }
  };

  const downloadDFormNew = async (row) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/exam-seating-plan/dform/${clientId}/${collegeId}/${row.exam_schedule_master_id}`,
        {
          responseType: "blob", // <-- important for binary files
        }
      );

      const fileName =
        `${row.exam_schedule_master_id}_${row.exam_month}_${row.exam_type_name}_DForm.pdf`.replace(
          /\s+/g,
          "_"
        );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(url, `_blank_${fileName}`);
      //const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading seating plan:", err);
    }
  };

  const downloadAbstractSeatingPlanNew = async (row) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/exam-seating-plan/abstractplan/${clientId}/${collegeId}/${row.exam_schedule_master_id}`,
        {
          responseType: "blob", // <-- important for binary files
        }
      );

      const fileName =
        `${row.exam_schedule_master_id}_${row.exam_month}_${row.exam_type_name}_Abstract_Seating_Plan.pdf`.replace(
          /\s+/g,
          "_"
        );
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(url, `_blank_${fileName}`);

      //const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);

      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading attendance:", err);
    }
  };

  const validate = () => {
    let newErrors = {};

    if (!form.exam_type_id) newErrors.exam_type_id = "Exam Type is required";
    if (!form.exam_mode_id) newErrors.exam_mode_id = "Exam Mode is required";
    if (!form.assessment_type) newErrors.assessment_type = "Assessment Type is required";

    if (!form.exam_month) newErrors.exam_month = "Exam Month is required";
    if (!form.exam_year) newErrors.exam_year = "Exam Year is required";

    if (!form.exam_start_date) newErrors.exam_start_date = "Start Date required";
    if (!form.exam_end_date) newErrors.exam_end_date = "End Date required";
    if (form.exam_start_date && form.exam_end_date && form.exam_start_date > form.exam_end_date) {
      newErrors.exam_end_date = "End Date cannot be before Start Date";
    }

    if (!form.start_enroll_date) newErrors.start_enroll_date = "Enroll Start Date required";
    if (!form.end_enroll_date) newErrors.end_enroll_date = "Enroll End Date required";
    if (
      form.start_enroll_date &&
      form.end_enroll_date &&
      form.start_enroll_date > form.end_enroll_date
    ) {
      newErrors.end_enroll_date = "Enroll End Date cannot be before Start Date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (validate()) {
      try {
        // Create one payload per exam_type_id
        const examTypeIds = Array.isArray(form.exam_type_id)
          ? form.exam_type_id
          : [form.exam_type_id];

        const payloads = examTypeIds.map((examTypeId) => ({
          ...form,
          exam_type_id: Number(examTypeId),
          client_id: Number(clientId),
          college_id: Number(collegeId),
          created_by: user_email,
        }));
        if (editing) {
          // Update only the first selected exam type (or adjust as per your logic)
          await axios.put(
            `${API_BASE_URL}/api/exam-external-schedule/${clientId}/${collegeId}/${editing.exam_schedule_master_id}`,
            payloads[0]
          );
          showSnackbar("Exam schedule updated successfully", "success");
        } else {
          // Insert multiple schedules (one per exam_type_id)
          await Promise.all(
            payloads.map((payload) =>
              axios.post(
                `${API_BASE_URL}/api/exam-external-schedule/${clientId}/${collegeId}`,
                payload
              )
            )
          );
          showSnackbar("Exam schedules created successfully", "success");
        }

        setOpen(false);
        fetchData();
      } catch (err) {
        console.error(err);
        showSnackbar(err?.response?.data?.message || "Save failed", "error");
      }
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 70,
        sortable: false,
        valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
      },
      // { field: "college_name", headerName: "College", flex: 1, minWidth: 100 },
      //{ field: "program_name", headerName: "Program", flex: 1, minWidth: 100 },
      { field: "exam_type_name", headerName: "Exam Type", flex: 1, minWidth: 100 },
      // { field: "exam_mode_name", headerName: "Exam Mode", flex: 1, minWidth: 100 },
      // { field: "assessment_type", headerName: "Assessment", flex: 1, minWidth: 100 },
      { field: "exam_month", headerName: "Month", flex: 1, minWidth: 100 },
      //{ field: "exam_year", headerName: "Year", flex: 0.6, minWidth: 80 },
      { field: "year_number", headerName: "Year", width: 80 },
      { field: "semester_number", headerName: "Semester", width: 90 },
      {
        field: "exam_start_date",
        headerName: "Start Date",
        flex: 1,
        minWidth: 90,
        renderCell: (params) => {
          const date = new Date(params.value);
          const formatted = `${String(date.getDate()).padStart(2, "0")}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}-${date.getFullYear()}`;
          return <span>{formatted}</span>;
        },
      },
      {
        field: "exam_end_date",
        headerName: "End Date",
        flex: 1,
        minWidth: 90,
        renderCell: (params) => {
          const date = new Date(params.value);
          const formatted = `${String(date.getDate()).padStart(2, "0")}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}-${date.getFullYear()}`;
          return <span>{formatted}</span>;
        },
      },

      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <ActionsMenu
            row={params.row}
            user={{
              college_id: user.college_id,
              client_id: user.client_id,
              user_email: user.user_email,
            }}
            onEdit={handleOpenEdit}
            onDelete={handleDelete}
            onCreate={fetchData}
            showSnackbar={showSnackbar}
            onGenerateTimetable={handleGenerateTimetable}
            onGenerateSeatingPlan={handleGenerateSeatingPlan}
          />
        ),
      },
    ],
    []
  );

  const handleDateChange = (date) => {
    if (date) {
      const formatted = dayjs(date).format("YYYY-MM-DD");
      let newDates = [...(form.exclude_dates || [])];

      if (newDates.includes(formatted)) {
        newDates = newDates.filter((d) => d !== formatted);
      } else {
        newDates.push(formatted);
      }

      // sort dates
      newDates.sort((a, b) => dayjs(a).diff(dayjs(b)));

      setForm({ ...form, exclude_dates: newDates });
    }
  };
  const months = Array.from({ length: 8 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - 2 + i);
    return date
      .toLocaleString("default", { month: "long", year: "numeric" })
      .toUpperCase()
      .replace(" ", "-");
  });

  const handleMonthChange = (e) => {
    const selectedMonth = e.target.value;
    const [month, year] = selectedMonth.split(" ");

    let finalYear = form.exam_year;
    if (!form.exam_year || form.exam_year !== year) {
      finalYear = year;
    }
    setForm({
      ...form,
      exam_month: selectedMonth,
      exam_year: finalYear,
    });
    validateField("exam_month", selectedMonth);
    validateField("exam_year", finalYear);
  };
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "exam_type_id":
        if (!value) error = "Exam type is required";
        break;
      case "exam_mode_id":
        if (!value) error = "Exam mode is required";
        break;
      case "assessment_type":
        if (!value.trim()) error = "Assessment type is required";
        break;
      case "exam_month":
        if (!value) error = "Exam month is required";
        break;
      case "exam_year":
        if (!value) error = "Exam year is required";
        else if (value < 2000 || value > 2100) error = "Enter a valid year";
        break;
      case "exam_start_date":
        if (!value) error = "Exam start date is required";
        else if (form.exam_end_date && value >= form.exam_end_date) {
          error = "Start date must be before End date";
        }
        break;
      case "exam_end_date":
        if (!value) error = "Exam end date is required";
        else if (form.exam_start_date && value <= form.exam_start_date) {
          error = "End date must be after Start date";
        }
        break;
      case "start_enroll_date":
        if (!value) error = "Enroll start date is required";
        else if (form.end_enroll_date && value >= form.end_enroll_date) {
          error = "Start date must be before End date";
        }
        break;
      case "end_enroll_date":
        if (!value) error = "Enroll end date is required";
        else if (form.start_enroll_date && value <= form.start_enroll_date) {
          error = "End date must be after Start date";
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };
  const handleChange = (e) => {
    const { name, value, selectedOptions } = e.target;

    // Special case: exam_type_id (multi-select)
    if (name === "exam_type_id") {
      const selected = Array.from(selectedOptions, (opt) => opt.value);
      setForm((prev) => ({ ...prev, [name]: selected }));
      validateField(name, selected); // validate array
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      validateField(name, value);
    }
  };
  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            EXAM EXTERNAL SCHEDULE MASTER (Prepare Separate Exam Schedule for each Semester)
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            sx={{ backgroundColor: "#1976d2", color: "#fff" }}
            onClick={handleMenuOpen}
          >
            Actions
          </Button>
          <Menu anchorEl={anchorEl} open={openMenu} onClose={handleMenuClose}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => {
                  handleMenuClose();
                  handleOpenAdd();
                }}
              >
                ADD EXAM SCHEDULE
              </Button>
            </MenuItem>
          </Menu>
          <TextField
            size="medium"
            placeholder="Search schedules"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, ml: 2, mr: 2, mb: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            page={page}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      {/* Dialog Add/Edit */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {editing ? "Edit Exam Schedule" : "Add Exam Schedule"}

          <IconButton
            onClick={() => setOpen(false)}
            aria-label="close"
            sx={{
              color: "#d32f2f",
              "&:hover": {
                backgroundColor: "rgba(211, 47, 47, 0.08)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Program (Optional for all programs)"
              name="program_id"
              select
              fullWidth
              value={form.program_id || ""}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ native: true, displayEmpty: true }}
              error={!!errors.program_id}
              helperText={errors.program_id}
            >
              <option value="">Select Program</option>
              {programs.map((p) => (
                <option key={p.program_id} value={p.program_id}>
                  {p.program_name}
                </option>
              ))}
            </TextField>
            {/* Exam Type Dropdown */}
            <ExamTypeSelect
              examTypes={examTypes} // array from your API or state
              form={form} // your form state
              setForm={setForm} // setter for form state
              errors={errors} // your validation errors
            />
            <Stack direction="row" spacing={2}>
              {/* Exam Mode Dropdown */}
              <TextField
                label="Exam Mode"
                name="exam_mode_id"
                select
                fullWidth
                value={form.exam_mode_id || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                error={!!errors.exam_mode_id}
                helperText={errors.exam_mode_id}
              >
                <option value="" disabled>
                  Select Exam Mode
                </option>
                {(examModes || []).map((mode) => (
                  <option key={mode.exam_mode_id} value={mode.exam_mode_id}>
                    {mode.exam_mode_name}
                  </option>
                ))}
              </TextField>

              <TextField
                label="Valuation Type"
                name="assessment_type"
                select
                fullWidth
                value={form.assessment_type || ""}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                error={!!errors.assessment_type}
                helperText={errors.assessment_type}
              >
                <option value="" disabled>
                  Select Assessment Type
                </option>
                {["ONLINE", "EXTERNAL", "INTERNAL"].map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Exam Month"
                name="exam_month"
                value={form.exam_month}
                onChange={handleMonthChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                error={!!errors.exam_month}
                helperText={errors.exam_month}
              >
                <option value="">Select Month</option>
                {months.map((month) => (
                  <option key={month} value={month}>
                    {month}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Exam Year"
                name="exam_year"
                type="number"
                value={form.exam_year}
                onChange={handleChange}
                error={!!errors.exam_year}
                helperText={errors.exam_year}
                fullWidth
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Exam Start Date"
                name="exam_start_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.exam_start_date}
                onChange={handleChange}
                error={!!errors.exam_start_date}
                helperText={errors.exam_start_date}
                fullWidth
              />
              <TextField
                label="Exam End Date"
                name="exam_end_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.exam_end_date}
                onChange={handleChange}
                error={!!errors.exam_end_date}
                helperText={errors.exam_end_date}
                fullWidth
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Enroll Start Date"
                name="start_enroll_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.start_enroll_date}
                onChange={handleChange}
                error={!!errors.start_enroll_date}
                helperText={errors.start_enroll_date}
                fullWidth
              />
              <TextField
                label="Enroll End Date"
                name="end_enroll_date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.end_enroll_date}
                onChange={handleChange}
                error={!!errors.end_enroll_date}
                helperText={errors.end_enroll_date}
                fullWidth
              />
            </Stack>
            {/* Exclude Dates Picker */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {/* Trigger Field */}
              <TextField
                label="Excluded Dates"
                value={(form.exclude_dates || []).join(", ") || "No dates selected"}
                fullWidth
                InputProps={{ readOnly: true }}
                onClick={() => setExcludeDialogOpen(true)} // ✅ use excludeDialogOpen
              />

              {/* Popup Dialog with Calendar */}
              <Dialog
                open={excludeDialogOpen} // ✅ separate state
                onClose={() => setExcludeDialogOpen(false)}
                maxWidth="md"
              >
                <DialogTitle>Select Exclude Dates</DialogTitle>
                <DialogContent>
                  <DateCalendar
                    value={null}
                    minDate={dayjs()}
                    maxDate={dayjs().add(3, "month")}
                    displayedMonths={3} // ✅ show 3 months side by side
                    onChange={handleDateChange}
                  />
                  <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                    {(form.exclude_dates || []).map((date) => (
                      <Chip
                        key={date}
                        label={date}
                        onDelete={() =>
                          setForm({
                            ...form,
                            exclude_dates: form.exclude_dates.filter((d) => d !== date),
                          })
                        }
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  <Box mt={2}>
                    <Button
                      onClick={() => setExcludeDialogOpen(false)}
                      variant="contained"
                      sx={{
                        backgroundColor: "#2e7d32",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "#1b5e20",
                          color: "#fff",
                        },
                      }}
                    >
                      Done
                    </Button>
                  </Box>
                </DialogContent>
              </Dialog>
            </LocalizationProvider>

            {/* Show Selected Dates as Chips instead of plain TextField */}
            <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
              {(form.exclude_dates || []).length === 0 ? (
                <TextField
                  label="Excluded Dates"
                  value="No dates selected"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              ) : (
                (form.exclude_dates || []).map((date) => (
                  <Chip
                    key={date}
                    label={date}
                    onDelete={() =>
                      setForm({
                        ...form,
                        exclude_dates: form.exclude_dates.filter((d) => d !== date),
                      })
                    }
                    color="primary"
                    variant="outlined"
                  />
                ))
              )}
            </Box>

            <TextField
              label="Workflow Status"
              select
              fullWidth
              value={form.workflow_status}
              onChange={(e) => setForm({ ...form, workflow_status: e.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="DRAFT">DRAFT</option>
              <option value="APPROVED">APPROVED</option>
              <option value="PUBLISHED">PUBLISHED</option>
              <option value="CLOSED">CLOSED</option>
            </TextField>
            <TextField
              label="Remarks"
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              fullWidth
              multiline
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            sx={{
              backgroundColor: "#ff0044",
              color: "#fff",
              "&:hover": { backgroundColor: "#b30015", color: "#fff" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              backgroundColor: "#007bff",
              color: "#fff",
              "&:hover": { backgroundColor: "#0056b3", color: "#fff" },
            }}
          >
            {editing ? "Update Exam Schedule" : "Create Exam Schedule"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
